/**
 * Admin → Affiliates (PRP-MCP-10 work item 1, G1, founder decision F1).
 *
 * The list: code, name, status, referrals, commissions, paid out, owed (and
 * whether owed meets the payout minimum). Create an affiliate (name, payout
 * email and method, optional AIEA account email, a code typed or generated),
 * pause or resume one. Each row opens `/admin/affiliates/:id`. Admin only on the
 * server: a non-admin gets 403 and sees "Admins only."
 *
 * PRP-MCP-11 G5: the Applications list (the anterotrail.com/affiliates form, with
 * the terms each applicant accepted). "Create affiliate" pre-fills the form and
 * sends `application_id`, so the affiliate carries the accepted version and time.
 * `?application=<id>` (the founder email's link) highlights that application.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { AdminAffiliatesAPI, affiliateErrText, fmtCents, termsText } from '../../lib/adminAffiliatesApi';
import type { AffiliateApplication, AffiliateRow } from '../../lib/adminAffiliatesApi';
import AffiliateApplications from './AffiliateApplications';

const input = 'rounded-md border border-border bg-background px-2 py-1 text-sm';

/** The Create affiliate form. */
const CreateForm: React.FC<{ onCreated: () => void; from: AffiliateApplication | null; onClearFrom: () => void }> = ({
  onCreated, from, onClearFrom,
}) => {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [payoutEmail, setPayoutEmail] = useState('');
  const [payoutMethod, setPayoutMethod] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!from) return;
    setName(from.name);
    setPayoutEmail(from.email);
    setMsg(null);
  }, [from]);

  const generate = async () => {
    try {
      setCode((await AdminAffiliatesAPI.newCode()).code);
    } catch (err) {
      setMsg(affiliateErrText(err, 'Could not generate a code.'));
    }
  };

  const submit = async () => {
    if (!name.trim()) {
      setMsg('A name is required.');
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      const made = await AdminAffiliatesAPI.create({
        name: name.trim(),
        ...(code.trim() ? { code: code.trim() } : {}),
        ...(payoutEmail.trim() ? { payout_email: payoutEmail.trim() } : {}),
        ...(payoutMethod.trim() ? { payout_method: payoutMethod.trim() } : {}),
        ...(userEmail.trim() ? { user_email: userEmail.trim() } : {}),
        ...(from ? { application_id: from.id } : {}),
      });
      setMsg(`Created ${made.name} with code ${made.code}.`);
      setName('');
      setCode('');
      setPayoutEmail('');
      setPayoutMethod('');
      setUserEmail('');
      onClearFrom();
      onCreated();
    } catch (err) {
      setMsg(affiliateErrText(err, 'Could not create the affiliate.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="space-y-2 rounded-md border border-border p-4" data-testid="create-affiliate">
      <h2 className="text-base font-semibold">New affiliate</h2>
      {from && (
        <p className="text-sm text-muted-foreground" data-testid="create-from-application">
          From the application of {from.name} ({from.email}) · {termsText(from.terms_version, from.terms_accepted_at)}{' '}
          <button type="button" className="underline" onClick={onClearFrom}>Clear</button>
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <input aria-label="Name" placeholder="Name" className={input} value={name} onChange={(e) => setName(e.target.value)} />
        <input aria-label="Code" placeholder="Code (6-12 letters/digits)" className={`${input} font-mono`} value={code}
          maxLength={12} onChange={(e) => setCode(e.target.value.toUpperCase())} />
        <button type="button" className="rounded-md border border-border px-2 py-1 text-sm" onClick={() => void generate()}>
          Generate code
        </button>
        <input aria-label="Payout email" placeholder="Payout email" className={input} value={payoutEmail}
          onChange={(e) => setPayoutEmail(e.target.value)} />
        <input aria-label="Payout method" placeholder="Payout method (PayPal, check…)" className={input} value={payoutMethod}
          onChange={(e) => setPayoutMethod(e.target.value)} />
        <input aria-label="AIEA account email" placeholder="Their AIEA account email (optional)" className={input}
          value={userEmail} onChange={(e) => setUserEmail(e.target.value)} />
        <button type="button" disabled={busy}
          className="rounded-md bg-primary px-3 py-1 text-sm text-primary-foreground disabled:opacity-50"
          onClick={() => void submit()}>
          Create
        </button>
      </div>
      {msg && <p className="text-sm text-muted-foreground">{msg}</p>}
    </section>
  );
};

/** The /admin/affiliates page. */
const AdminAffiliatesPage: React.FC = () => {
  const [rows, setRows] = useState<AffiliateRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [from, setFrom] = useState<AffiliateApplication | null>(null);
  const [appsKey, setAppsKey] = useState(0);
  const [params] = useSearchParams();

  const load = useCallback(async () => {
    setError(null);
    try {
      setRows((await AdminAffiliatesAPI.list()).affiliates);
    } catch (err) {
      setError(affiliateErrText(err, 'Could not load affiliates.'));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const toggle = async (a: AffiliateRow) => {
    const next = a.status === 'active' ? 'paused' : 'active';
    if (next === 'paused' && !window.confirm(`Pause ${a.name}? New signups with ${a.code} will not be referred.`)) return;
    try {
      await AdminAffiliatesAPI.setStatus(a.id, next);
      await load();
    } catch (err) {
      setError(affiliateErrText(err, 'Could not change the status.'));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold">Affiliates</h1>
        <Link to="/admin" className="text-sm text-muted-foreground underline">Admin</Link>
      </div>
      <p className="text-sm text-muted-foreground">
        20% of every payment a referred user makes for 12 months from signup. Owed is the unpaid total; pay above the
        minimum, then Mark paid on the affiliate&apos;s page.
      </p>
      {error && <p className="text-sm font-medium text-red-500">{error}</p>}
      <CreateForm from={from} onClearFrom={() => setFrom(null)}
        onCreated={() => { setAppsKey((k) => k + 1); void load(); }} />
      <AffiliateApplications onPick={setFrom} highlightId={params.get('application')} reloadKey={appsKey} />
      {rows === null && !error && <p className="text-sm text-muted-foreground">Loading…</p>}
      {rows && rows.length === 0 && <p className="text-sm text-muted-foreground">No affiliates yet.</p>}
      {rows && rows.length > 0 && (
        <table className="w-full text-sm" data-testid="affiliates-table">
          <thead className="text-left text-xs text-muted-foreground">
            <tr>
              <th className="p-1">Code</th><th className="p-1">Name</th><th className="p-1">Status</th>
              <th className="p-1 text-right">Referrals</th><th className="p-1 text-right">Commissions</th>
              <th className="p-1 text-right">Paid out</th><th className="p-1 text-right">Owed</th><th className="p-1" />
            </tr>
          </thead>
          <tbody>
            {rows.map((a) => (
              <tr key={a.id} className="border-t border-border">
                <td className="p-1 font-mono"><Link to={`/admin/affiliates/${a.id}`} className="underline">{a.code}</Link></td>
                <td className="p-1">{a.name}</td>
                <td className="p-1">{a.status === 'active' ? 'Active' : 'Paused'}</td>
                <td className="p-1 text-right">{a.referrals}</td>
                <td className="p-1 text-right">{fmtCents(a.commissions_cents)}</td>
                <td className="p-1 text-right">{fmtCents(a.paid_out_cents)}</td>
                <td className="p-1 text-right">
                  {fmtCents(a.owed_cents)}{a.meets_minimum ? '' : ' (under minimum)'}
                </td>
                <td className="p-1 text-right">
                  <button type="button" className="rounded-md border border-border px-2 py-0.5 text-xs" onClick={() => void toggle(a)}>
                    {a.status === 'active' ? `Pause ${a.code}` : `Resume ${a.code}`}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default AdminAffiliatesPage;
